import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Clock, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Hours Distribution Chart - Donut chart showing hours breakdown
 */
const HoursDistributionChart = ({ distribution, loading, periodLabel }) => {
  const navigate = useNavigate();

  const handleViewTimeEntries = () => {
    navigate('/home/timeentries');
  };
  const COLORS = {
    regular: '#22c55e',
    overtime: '#f59e0b',
    break: '#6366f1',
  };

  const chartData = distribution
    ? [
        { name: 'Regular', value: distribution.regular_hours || 0, color: COLORS.regular },
        { name: 'Overtime', value: distribution.overtime_hours || 0, color: COLORS.overtime },
        { name: 'Breaks', value: distribution.break_hours || 0, color: COLORS.break },
      ].filter(item => item.value > 0)
    : [];

  const totalHours = distribution?.total_hours || 0;
  const targetHours = distribution?.target_hours || 0;
  const variance = targetHours > 0 ? ((totalHours - targetHours) / targetHours * 100).toFixed(1) : 0;

  const formatHours = (hours) => {
    if (!hours) return '0h';
    return `${Math.round(hours)}h`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
          <p className="font-medium text-gray-800">{data.name}</p>
          <p className="text-sm text-gray-600">{formatHours(data.value)}</p>
          <p className="text-xs text-gray-400">
            {totalHours > 0 ? Math.round((data.value / totalHours) * 100) : 0}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-36 bg-gray-200 rounded-full w-36 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-green-600" />
          <h3 className="text-base font-semibold text-gray-800">Hours Distribution</h3>
        </div>
        <div className="flex items-center gap-2">
          {periodLabel && (
            <span className="text-xs text-gray-1000">{periodLabel}</span>
          )}
          <button
            onClick={handleViewTimeEntries}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5"
          >
            View All <ChevronRight size={12} />
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-4">
        {/* Donut Chart */}
        <div className="relative w-36 h-36">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={65}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center Label */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold text-gray-800">
              {formatHours(totalHours)}
            </span>
            <span className="text-xs text-gray-1000">Total</span>
          </div>
        </div>

        {/* Legend & Stats */}
        <div className="flex-1 space-y-2">
          {/* Legend */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.regular }}></span>
                <span className="text-sm text-gray-600">Regular Hours</span>
              </div>
              <span className="text-sm font-semibold text-gray-800">
                {formatHours(distribution?.regular_hours)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.overtime }}></span>
                <span className="text-sm text-gray-600">Overtime</span>
              </div>
              <span className="text-sm font-semibold text-gray-800">
                {formatHours(distribution?.overtime_hours)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.break }}></span>
                <span className="text-sm text-gray-600">Break Time</span>
              </div>
              <span className="text-sm font-semibold text-gray-800">
                {formatHours(distribution?.break_hours)}
              </span>
            </div>
          </div>

          {/* Target Comparison */}
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-1000">Target</span>
              <span className="font-medium text-gray-700">{formatHours(targetHours)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-gray-1000">vs Target</span>
              <span className={`font-semibold ${
                parseFloat(variance) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {parseFloat(variance) >= 0 ? '+' : ''}{variance}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HoursDistributionChart;
